import { useTranslation } from 'react-i18next'

import useSessionUser from '@/modules/access/hooks/useSessionUser'
import { Accordion } from '@/modules/shared/components/accordion/Accordion'
import CountBadge from '@/modules/shared/components/count-badge/CountBadge'
import { displayBadgeCount } from '@/modules/shared/components/count-badge/displayBadgeCount'
import { useGetDashboardStats } from '@/modules/shared/hooks/useGetDashboardStats'
import { BadgeIcon } from '@/modules/shared/icons-special/BadgeIcon'
import { InvoiceIcon } from '@/modules/shared/icons/InvoiceIcon'
import { PlusIcon } from '@/modules/shared/icons/PlusIcon'
import SideNavLink, { SideNavLinkItem } from '@/modules/shared/layouts/side-navigation/SideNavLink'
import { SideBarItem } from '@/modules/shared/layouts/side-navigation/purchaser-navigation/PurchaserNavigation'
import { Themes } from '@/modules/shared/types/Themes'

interface CreditNotesSideBarItemProps extends SideBarItem {
  creditNoteRoutesMatch: boolean
}

export const CreditNotesSideBarItem = ({
  creditNoteRoutesMatch,
  openAccordionKey,
  changeOpenAccordionKey,
  onCloseDrawer,
  sideBarIndexKey,
}: CreditNotesSideBarItemProps) => {
  const { t } = useTranslation()
  const { dashboardStats } = useGetDashboardStats()

  const { userPermissions, currentOrganisation } = useSessionUser()

  const getSideNavLinkItems = () => {
    const sideNavItems: SideNavLinkItem[] = []
    if (userPermissions?.PURCHASER_CAN_MANAGE_INVOICES_AND_CREDIT_NOTES) {
      sideNavItems.push({
        key: t('general.newCreditNote', 'New Credit Note'),
        target: '/credit-notes/create',
        icon: <PlusIcon className="size-6 text-gray-500" />,
      })
    }
    sideNavItems.push({
      key: t('general.viewAll', 'View All'),
      target: '/credit-notes',
    })
    if (currentOrganisation?.featureFlags?.newApprovalWorkflows) {
      sideNavItems.push({
        key: t('general.awaitingMyApproval', 'Awaiting My Approval'),
        target: '/credit-notes/awaiting-my-approval',
        icon: (
          <CountBadge
            displayText={displayBadgeCount(dashboardStats?.workflowCreditNotesAwaitingApprovalCount ?? 0, 25)}
          />
        ),
      })
    }
    sideNavItems.push({ key: t('general.exportCenter', 'Export Center'), target: '/invoices/export-center' })
    return sideNavItems
  }

  return (
    <Accordion
      titlePrefixIcon={
        <BadgeIcon
          isBadged={
            !!dashboardStats?.workflowCreditNotesAwaitingApprovalCount &&
            !!currentOrganisation?.featureFlags?.newApprovalWorkflows
          }
        >
          <InvoiceIcon className="size-7" />
        </BadgeIcon>
      }
      title={t('general.creditNotes', 'Credit Notes')}
      activeColor={creditNoteRoutesMatch ? Themes.Primary : undefined}
      defaultOpen={openAccordionKey === sideBarIndexKey}
      handleTriggerClick={() => changeOpenAccordionKey(sideBarIndexKey)}
      buttonClassName="min-h-11 px-3"
      keepActiveColor
    >
      <SideNavLink
        onCloseDrawer={() => onCloseDrawer()}
        testId="credit-note-navigation"
        sideNavLinkItems={getSideNavLinkItems()}
      />
    </Accordion>
  )
}
